import React from 'react'
import { useTranslation } from 'react-i18next'

import { Badge, BadgeProps, BadgeSize, BadgeType } from '@ui/components/Badge/Badge'

import { CampaignStatusEnum } from '@model/CampaignStatusEnum'
import { TestStatusEnum } from '@model/ClientTestAnswerDto'
import { RebateApprovalStatus } from '@model/RebateApprovalsDto'
import { SubscriptionStatus } from '@model/SubscriptionsDto'
import { TransactionStatus } from '@model/TransactionStatus'

type StatusKey = string | number | symbol

export type StatusBadgeMapType<K extends StatusKey> = Record<K, { type: BadgeType; label: string }>

type StatusBadgeProps<K extends StatusKey> = {
  id: K
  badgeMap: StatusBadgeMapType<K>
  onClick?: () => void
  size?: BadgeSize
} & Pick<BadgeProps, 'leftIcon' | 'rightIcon'>

export function StatusBadge<K extends StatusKey>({
  id,
  badgeMap,
  leftIcon,
  rightIcon,
  onClick,
  size = 'sm',
}: StatusBadgeProps<K>) {
  const { t } = useTranslation()
  const badgeConfig = badgeMap[id]
  if (!badgeConfig) return null

  return (
    <Badge
      leftIcon={leftIcon}
      rightIcon={rightIcon}
      type={badgeConfig.type}
      contrast='low'
      size={size}
      onClick={onClick}
    >
      {t(badgeConfig.label)}
    </Badge>
  )
}

export const TestBadgeMap: StatusBadgeMapType<TestStatusEnum> = {
  [TestStatusEnum.Pending]: { type: 'blue', label: 'Pending' },
  [TestStatusEnum.Approved]: { type: 'green', label: 'Approved' },
  [TestStatusEnum.Declined]: { type: 'danger', label: 'Declined' },
}

export const TransactionBadgeMap: StatusBadgeMapType<TransactionStatus> = {
  [TransactionStatus.None]: { type: 'danger', label: 'Pending' },
  [TransactionStatus.AwaitingApproval]: { type: 'blue', label: 'AwaitingApproval' },
  [TransactionStatus.Pending]: { type: 'blue', label: 'Pending' },
  [TransactionStatus.Completed]: { type: 'green', label: 'Completed' },
  [TransactionStatus.Cancelled]: { type: 'yellow', label: 'Cancelled' },
  [TransactionStatus.Declined]: { type: 'danger', label: 'Declined' },
  [TransactionStatus.Failed]: { type: 'danger', label: 'Failed' },
  [TransactionStatus.Reversed]: { type: 'danger', label: 'Reversed' },
}

export const SubscriptionBadgeMap: StatusBadgeMapType<SubscriptionStatus> = {
  [SubscriptionStatus.PENDING]: { type: 'blue', label: 'Subscriptions.Pending' },
  [SubscriptionStatus.FAILED]: { type: 'danger', label: 'Subscriptions.Failed' },
  [SubscriptionStatus.CANCELLED]: { type: 'danger', label: 'Subscriptions.Cancelled' },
  [SubscriptionStatus.IN_PROCESS_CANCEL]: {
    type: 'yellow',
    label: 'Subscriptions.In Process Cancel',
  },
  [SubscriptionStatus.ACTIVE]: { type: 'green', label: 'Subscriptions.Active' },
  [SubscriptionStatus.COMPLETED]: { type: 'green', label: 'Subscriptions.Completed' },
  [SubscriptionStatus.DEDUCTING_FUNDS]: { type: 'blue', label: 'Subscriptions.Deducting Funds' },
  [SubscriptionStatus.IN_PROCESS]: { type: 'blue', label: 'Subscriptions.In Process' },
}

export const IBContestBadgeMap: StatusBadgeMapType<CampaignStatusEnum> = {
  [CampaignStatusEnum.Enabled]: { type: 'green', label: 'Active' },
  [CampaignStatusEnum.Disabled]: { type: 'black', label: 'Ended' },
  [CampaignStatusEnum.Paused]: { type: 'purple', label: 'Paused' },
}

export const IBRebateApprovalsBadgeMap: StatusBadgeMapType<RebateApprovalStatus> = {
  [RebateApprovalStatus.New]: { type: 'black', label: 'New' },
  [RebateApprovalStatus.Pending]: { type: 'blue', label: 'Pending' },
  [RebateApprovalStatus.Approved]: { type: 'green', label: 'Approved' },
  [RebateApprovalStatus.Rejected]: { type: 'danger', label: 'Rejected' },
  [RebateApprovalStatus.Failed]: { type: 'danger', label: 'Failed' },
  [RebateApprovalStatus.Reversed]: { type: 'danger', label: 'Reversed' },
}
